import React, { useState, useMemo } from 'react';
import { Search, UserCheck, X } from 'lucide-react';
import { usePlan } from './PlanContext';

export default function GuestsSidePanel({ style }) {
  const { invites, groups, mainSeg, assignMode, setAssignMode, resolvedTables } = usePlan();
  const [search, setSearch]         = useState('');
  const [filterGroup, setFilterGroup] = useState('');

  const groupMap = useMemo(() => {
    const m = {}; groups.forEach(g => { m[g.id] = g; }); return m;
  }, [groups]);

  const tableMap = useMemo(() => {
    const m = {}; resolvedTables.forEach(t => { m[t.id] = t; }); return m;
  }, [resolvedTables]);

  // Invités confirmés sur le segment principal
  const confirmed = useMemo(() => {
    return invites.filter(inv => {
      if (!mainSeg) return true;
      const status = inv.seg_status?.[mainSeg.id];
      return status === 'yes' || status === 'pending'; // montrer aussi les pending
    });
  }, [invites, mainSeg]);

  const filtered = useMemo(() => {
    let list = confirmed;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.nom.toLowerCase().includes(q));
    }
    if (filterGroup) {
      list = list.filter(i => i.group_id === filterGroup);
    }
    return list;
  }, [confirmed, search, filterGroup]);

  const unplaced = filtered.filter(i => !i.table_id);
  const placed   = filtered.filter(i =>  i.table_id);

  return (
    <div style={{
      width: 290, flexShrink: 0,
      background: '#FFFFFF', borderLeft: '1px solid #E5E7EB',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Montserrat', sans-serif",
      ...style,
    }}>
      {/* Header */}
      <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #F3F4F6' }}>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 8 }}>
          Invités à placer
        </p>
        {/* Recherche */}
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <Search size={12} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher…"
            style={{
              width: '100%', padding: '5px 8px 5px 24px',
              borderRadius: 6, fontSize: 11,
              border: '1px solid #D1D5DB', outline: 'none',
              fontFamily: "'Montserrat', sans-serif",
              boxSizing: 'border-box',
            }}
          />
        </div>
        {/* Filtre groupe */}
        {groups.length > 0 && (
          <select
            value={filterGroup}
            onChange={e => setFilterGroup(e.target.value)}
            style={{
              width: '100%', padding: '4px 6px', borderRadius: 6, fontSize: 11,
              border: '1px solid #D1D5DB', outline: 'none', color: '#374151',
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            <option value="">Tous les groupes</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Mode assignation actif */}
      {assignMode && (
        <div style={{
          padding: '6px 12px', background: 'rgba(212,175,55,.1)',
          borderBottom: '1px solid rgba(212,175,55,.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 11, color: '#C58B19', fontWeight: 600 }}>
            ✦ Tapez un siège libre pour placer
          </span>
          <button
            onClick={() => setAssignMode(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Listes */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {/* Non placés */}
        {unplaced.length > 0 && (
          <ListSection
            title={`Non placés (${unplaced.length})`}
            guests={unplaced}
            groupMap={groupMap}
            assignMode={assignMode}
            setAssignMode={setAssignMode}
          />
        )}
        {/* Déjà placés */}
        {placed.length > 0 && (
          <ListSection
            title={`Déjà placés (${placed.length})`}
            guests={placed}
            groupMap={groupMap}
            tableMap={tableMap}
            assignMode={assignMode}
            setAssignMode={setAssignMode}
          />
        )}
        {filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, marginTop: 24 }}>
            Aucun invité trouvé
          </p>
        )}
      </div>
    </div>
  );
}

function ListSection({ title, guests, groupMap, tableMap, assignMode, setAssignMode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <p style={{
        fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: '#C4B5A0',
        padding: '0 12px', marginBottom: 4,
      }}>{title}</p>
      {guests.map(guest => (
        <GuestRow
          key={guest.id}
          guest={guest}
          group={groupMap[guest.group_id]}
          tableName={tableMap?.[guest.table_id]?.label}
          isAssigning={assignMode === guest.id}
          onClick={() => setAssignMode(assignMode === guest.id ? null : guest.id)}
        />
      ))}
    </div>
  );
}

function GuestRow({ guest, group, tableName, isAssigning, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 12px', cursor: 'pointer',
        background: isAssigning ? 'rgba(212,175,55,.12)' : 'transparent',
        borderLeft: isAssigning ? '3px solid #D4AF37' : '3px solid transparent',
        transition: 'background .1s',
      }}
    >
      {/* Pastille couleur groupe */}
      <div style={{
        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
        background: group?.color || '#D4AF37',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: 12, fontWeight: 600, color: '#1F2937',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          margin: 0,
        }}>
          {guest.nom}
          {guest.age === 'kid'   && <span style={{ fontSize: 9, marginLeft: 4 }}>👶</span>}
          {guest.plus_one?.name  && <span style={{ fontSize: 9, marginLeft: 4, color: '#9CA3AF' }}>+1</span>}
        </p>
        <p style={{ fontSize: 9, color: '#9CA3AF', margin: 0 }}>
          {tableName || group?.name || ''}
        </p>
      </div>
      {isAssigning && (
        <span style={{ fontSize: 9, color: '#D4AF37', fontWeight: 700 }}>✦</span>
      )}
    </div>
  );
}
